interface DateRangeFilterProps {
  /** Current start-date value (bound to the first date input). */
  startDate: string;
  /** Current end-date value (bound to the second date input). */
  endDate: string;
  /** Whether there are unapplied date changes; drives Apply button enabled/style/title. */
  hasPendingDateChanges: boolean;
  /** onChange handler for the start-date input (verbatim from the call site). */
  onStartDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** onChange handler for the end-date input (verbatim from the call site). */
  onEndDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** onKeyDown handler for the start-date input (verbatim from the call site). */
  onStartDateKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** onKeyDown handler for the end-date input (verbatim from the call site). */
  onEndDateKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Click handler for the Apply button (verbatim from the call site). */
  onApply: () => void;
}

/**
 * Start/end date inputs plus the Apply button shared by the report/log pages
 * (GroomingReportPage, MoodReportPage, RandomAttendanceLogsPage). The markup,
 * classes, and Apply enabled/disabled logic were previously inlined identically
 * in each page; this component preserves them exactly while taking the varying
 * per-page state and handlers as props.
 */
export default function DateRangeFilter({
  startDate,
  endDate,
  hasPendingDateChanges,
  onStartDateChange,
  onEndDateChange,
  onStartDateKeyDown,
  onEndDateKeyDown,
  onApply,
}: DateRangeFilterProps) {
  return (
    <>
      <input
        type="date"
        value={startDate}
        onChange={onStartDateChange}
        onKeyDown={onStartDateKeyDown}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      />

      <input
        type="date"
        value={endDate}
        onChange={onEndDateChange}
        onKeyDown={onEndDateKeyDown}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      />

      <button
        onClick={onApply}
        disabled={!hasPendingDateChanges || !startDate || !endDate}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          hasPendingDateChanges
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        } disabled:opacity-50`}
        title={hasPendingDateChanges ? 'Apply the date range to reload the report' : 'No changes to apply'}
      >
        Apply
      </button>
    </>
  );
}
